import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook,
  faCheckCircle,
  faCertificate,
  faCalendarAlt,
  faCalendarPlus,
  faClock,
  faChalkboardTeacher,
  faArrowRight,
  faGlobe,
  faCircleExclamation,
  faPen,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './Schedule.css';

const PAST_PREVIEW = 5;

const toDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
};

const icsDate = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

const MySchedule = ({ userData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAllPast, setShowAllPast] = useState(false);

  const studentId = userData?.id;
  const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const name = userData?.fullName || 'a RoboMind student';

  const waUrl = (text) =>
    `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;

  // ---------- Data (same endpoint) ----------
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/student/schedule/${studentId}`);
        if (!cancelled) setData(response.data);
      } catch (err) {
        if (!cancelled) setError('We couldn’t load your schedule.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  // Upcoming: soonest first. Past: most recent first, numbered from the first class.
  const upcoming = useMemo(
    () =>
      (data?.remainingschedule || [])
        .map((s) => ({ ...s, date: toDate(s.ClassDateTime) }))
        .filter((s) => s.date)
        .sort((a, b) => a.date - b.date),
    [data]
  );

  const past = useMemo(() => {
    const list = (data?.doneschedule || [])
      .map((s) => ({ ...s, date: toDate(s.ClassDateTime) }))
      .filter((s) => s.date)
      .sort((a, b) => b.date - a.date);
    return list.map((s, i) => ({ ...s, number: list.length - i }));
  }, [data]);

  const downloadAllIcs = () => {
    if (!upcoming.length) return;
    const events = upcoming.flatMap((s, i) => {
      const end = new Date(s.date.getTime() + 60 * 60000);
      return [
        'BEGIN:VEVENT',
        `UID:robomind-${studentId}-${icsDate(s.date)}-${i}@robomind-edu.com`,
        `DTSTAMP:${icsDate(new Date())}`,
        `DTSTART:${icsDate(s.date)}`,
        `DTEND:${icsDate(end)}`,
        'SUMMARY:RoboMind coding class',
        `DESCRIPTION:Live class${s.teacher ? ` with ${s.teacher}` : ''}. Join from ${window.location.origin}/student/dashboard`,
        'BEGIN:VALARM',
        'TRIGGER:-PT15M',
        'ACTION:DISPLAY',
        'DESCRIPTION:Your class starts in 15 minutes',
        'END:VALARM',
        'END:VEVENT',
      ];
    });
    const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//RoboMind//Schedule//EN', ...events, 'END:VCALENDAR'].join('\r\n');
    const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'robomind-schedule.ics';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- Loading ----------
  if (loading || !studentId) {
    return (
      <div className="rmms" aria-busy="true">
        <div className="rmms-inner">
          <div className="rmms-skel rmms-skel--title" />
          <div className="rmms-stats">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rmms-skel rmms-skel--stat" />
            ))}
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} className="rmms-skel rmms-skel--row" />
          ))}
        </div>
      </div>
    );
  }

  // ---------- Error ----------
  if (error || !data) {
    return (
      <div className="rmms">
        <div className="rmms-state">
          <FontAwesomeIcon icon={faCircleExclamation} className="rmms-state-icon is-error" />
          <h1 className="rmms-state-title">Something went wrong</h1>
          <p>{error || 'We couldn’t find your schedule.'} Please try again in a moment.</p>
          <button type="button" className="rmms-btn" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const remaining = Number(data.ongoingclass) || 0;
  const pastShown = showAllPast ? past : past.slice(0, PAST_PREVIEW);

  return (
    <div className="rmms">
      <div className="rmms-inner">
        <header className="rmms-head">
          <span className="rmms-eyebrow">My Schedule</span>
          <h1 className="rmms-title">
            Your <span>classes</span>
          </h1>
          <p className="rmms-sub">
            <FontAwesomeIcon icon={faGlobe} /> All times are in your time zone ({timeZone}).
          </p>
        </header>

        {/* ---------- Stats ---------- */}
        <div className="rmms-stats">
          <div className={`rmms-stat ${remaining <= 2 ? 'is-low' : ''}`}>
            <span className="rmms-stat-icon">
              <FontAwesomeIcon icon={faBook} />
            </span>
            <div>
              <strong>{remaining}</strong>
              <span>{remaining === 1 ? 'session left in your package' : 'sessions left in your package'}</span>
              {remaining <= 2 && (
                <a
                  className="rmms-renew"
                  href={waUrl(`Hi! I'm ${name} and I have ${remaining} session${remaining === 1 ? '' : 's'} left. I'd like to renew my package.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Renew package <FontAwesomeIcon icon={faArrowRight} />
                </a>
              )}
            </div>
          </div>
          <div className="rmms-stat">
            <span className="rmms-stat-icon">
              <FontAwesomeIcon icon={faCheckCircle} />
            </span>
            <div>
              <strong>{data.nbofclasstaken || 0}</strong>
              <span>classes completed</span>
            </div>
          </div>
          <div className="rmms-stat">
            <span className="rmms-stat-icon">
              <FontAwesomeIcon icon={faCertificate} />
            </span>
            <div>
              <strong>{data.nbofcertificates || 0}</strong>
              <span>certificates earned</span>
            </div>
          </div>
        </div>

        {/* ---------- Upcoming ---------- */}
        <section className="rmms-section" aria-labelledby="rmms-up-title">
          <div className="rmms-section-head">
            <h2 id="rmms-up-title" className="rmms-section-title">
              Upcoming <span className="rmms-count">{upcoming.length}</span>
            </h2>
            {upcoming.length > 0 && (
              <button type="button" className="rmms-ghost" onClick={downloadAllIcs}>
                <FontAwesomeIcon icon={faCalendarPlus} /> Add all to calendar
              </button>
            )}
          </div>

          {upcoming.length === 0 ? (
            <div className="rmms-empty">
              <FontAwesomeIcon icon={faCalendarAlt} className="rmms-empty-icon" />
              <h3>No upcoming classes</h3>
              <p>Message us and we’ll book your next classes at times that suit you.</p>
              <a className="rmms-btn rmms-btn--wa" href={waUrl(`Hi! I'm ${name} and I'd like to schedule my next classes.`)} target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faWhatsapp} /> Schedule on WhatsApp
              </a>
            </div>
          ) : (
            <ul className="rmms-list">
              {upcoming.map((s, i) => {
                const weekday = s.date.toLocaleDateString('en-US', { weekday: 'short' });
                const day = s.date.getDate();
                const month = s.date.toLocaleDateString('en-US', { month: 'short' });
                const time = s.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                const longDate = s.date.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' });
                return (
                  <li key={`${s.ClassDateTime}-${i}`} className={`rmms-row ${i === 0 ? 'is-next' : ''}`}>
                    <div className="rmms-date" aria-hidden="true">
                      <span>{weekday}</span>
                      <strong>{day}</strong>
                      <span>{month}</span>
                    </div>
                    <div className="rmms-row-body">
                      <h3>
                        {i === 0 ? 'Next class' : `Class ${i + 1}`}
                        {i === 0 && <span className="rmms-next-tag">Next</span>}
                      </h3>
                      <p className="rmms-row-meta">
                        <span>
                          <FontAwesomeIcon icon={faClock} /> {time}
                        </span>
                        <span>
                          <FontAwesomeIcon icon={faChalkboardTeacher} /> {s.teacher || 'Your teacher'}
                        </span>
                        <span className="rmms-sr">{longDate}</span>
                      </p>
                    </div>
                    <a
                      className="rmms-change"
                      href={waUrl(`Hi! I'm ${name}. I'd like to change my class on ${longDate} at ${time}.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Change the class on ${longDate} at ${time}`}
                    >
                      <FontAwesomeIcon icon={faPen} /> Change
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ---------- Past ---------- */}
        <section className="rmms-section" aria-labelledby="rmms-past-title">
          <div className="rmms-section-head">
            <h2 id="rmms-past-title" className="rmms-section-title">
              Completed <span className="rmms-count is-done">{past.length}</span>
            </h2>
          </div>

          {past.length === 0 ? (
            <p className="rmms-empty rmms-empty--small">Your finished classes will appear here.</p>
          ) : (
            <>
              <ul className="rmms-list rmms-list--past">
                {pastShown.map((s, i) => (
                  <li key={`${s.ClassDateTime}-${i}`} className="rmms-row is-past">
                    <span className="rmms-past-num">#{s.number}</span>
                    <div className="rmms-row-body">
                      <p className="rmms-row-meta">
                        <span>
                          <FontAwesomeIcon icon={faCalendarAlt} />{' '}
                          {s.date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span>
                          <FontAwesomeIcon icon={faClock} /> {s.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span>
                          <FontAwesomeIcon icon={faChalkboardTeacher} /> {s.teacher || 'Your teacher'}
                        </span>
                      </p>
                    </div>
                    <span className="rmms-done">
                      <FontAwesomeIcon icon={faCheckCircle} /> Done
                    </span>
                  </li>
                ))}
              </ul>
              {past.length > PAST_PREVIEW && (
                <button type="button" className="rmms-more" onClick={() => setShowAllPast((v) => !v)} aria-expanded={showAllPast}>
                  {showAllPast ? 'Show less' : `Show all ${past.length}`}
                  <FontAwesomeIcon icon={faChevronDown} className={showAllPast ? 'is-up' : ''} />
                </button>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default MySchedule;